import { FeaResult } from '../rendering/fea-result';
import { Scale } from './../../template/color-scale/models/scale';
import { Injectable } from '@angular/core';
import { GraphicsMaterials } from 'src/app/digital-twin/graphics/graphics-materials';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { ResultScale } from '../models/result-scale';
import { AssetManagerFea } from 'src/app/asset-manager/asset-manager-fea';
import { FeaProcessService } from './fea-process.service';
import { FeaSingleColorConfiguration, FeaSpectralColorConfiguration } from '../rendering/fea-color-configuration';
import { TemplateService } from 'src/app/template/template.service';
import { ProcessResult } from '../models/results/process-result';
import { FeaMeshRenderer } from '../rendering/fea-mesh-renderer';
import { Graphics } from 'src/app/digital-twin/graphics/graphics';

@Injectable({
  providedIn: 'root'
})
export class FeaResultService {

  public title: string;
  public scale: Scale;
  public useScale: boolean = false;
  public loadingResult: boolean = false;
  public currentProcessResult: ProcessResult | null = null;

  public FeaResult: FeaResult;

  private currentProcessName: string;
  private currentByNode: boolean;
  private currentVectorName: string;

  private feaMeshRenderer: FeaMeshRenderer;
  public showMesh: boolean = false;
  private assetStateSubscription: any = null;

  constructor(
    private readonly graphicsMaterials: GraphicsMaterials,
    private readonly assetManager: AssetManager,
    private readonly assetManagerFea: AssetManagerFea,
    private readonly feaProcessService: FeaProcessService,
    private templateService: TemplateService,
    private readonly graphics: Graphics
  ) {
    this.feaMeshRenderer = new FeaMeshRenderer();
    this.assetManagerFea.isFeaResultActive$.subscribe((isActive) => {
      this.handleActivationChange(isActive).catch(err => {
        console.error('Error handling fea result activation change:', err);
      });
    });

    this.assetManagerFea.dateChangedWithResultActivation$.subscribe(([dateChanged, isActive]) => {
      this.handleDateChange(dateChanged, isActive).catch(err => {
        console.error('Error handling fea date change:', err);
      });
    });
  }

  public setCurrentResultParameters(processName: string, vectorName: string, byNode: boolean = false) : void {
    this.unsetFeaResult();
    this.resetFeaResult();

    // Pass FEA Actions to QrCode
    this.templateService.toggleFeaActionService(true, ("FEA" + processName.substring(0,3)))

    this.currentProcessName = processName;
    this.currentVectorName = vectorName;
    this.currentByNode = byNode;
  }

  private unsetFeaResult() {
    if(this.assetManager.asset)
      this.graphicsMaterials.defaultViewMode(this.assetManager.asset.GetStructureRegions());
  }

  private resetFeaResult() : void {
    this.unsetFeaResult();
    this.clearMeshLines();
    this.FeaResult = null
    this.scale = null
    this.useScale = false;
    this.title = null
    this.currentProcessResult = null
  }

  private isCurrentModeFeaResultMode(): boolean {
    const feaModes = ['FEAResults', 'PlatePanels', 'StiffenedPanels', 'LargePanels'];
    return feaModes.includes(this.assetManager.asset.viewMode);
  }

  private clearFeaDataOnly() : void {
    this.clearMeshLines();
    this.FeaResult = null;
    this.scale = null;
    this.loadingResult = false;
  }

  private async handleActivationChange(isActive: boolean) {
    if (isActive) {
      await this.activateResult();
    }
    else {
      this.deactivateFeaResult();
    }
  }

  private async activateResult() : Promise<void> {
    if (this.isResultAvailable()) {
      this.colorFeaResult();
    }
    else {
      await this.fetchAndRenderCurrentResult();
    }
    
    this.subscribeToAssetStateChanges();
  }

  private deactivateFeaResult() : void {
    this.unsetFeaResult();
    this.clearMeshLines();
    this.unsubscribeFromAssetStateChanges();
  }

  public isResultAvailable() : boolean {
    switch (this.assetManagerFea.latestFeaMode) {
      case 'FEAResults':
        return !!this.FeaResult && this.currentProcessName != 'PanelRecognition'; // TODO: temporary while panel results are not unified with other results
      case 'PlatePanels':
        return !!this.FeaResult && this.currentProcessName == 'PanelRecognition' && this.currentVectorName == "Plate";
      case 'StiffenedPanels':
        return !!this.FeaResult && this.currentProcessName == 'PanelRecognition' && this.currentVectorName == "Stiffened";
      case 'LargePanels':
        return !!this.FeaResult && this.currentProcessName == 'PanelRecognition' && this.currentVectorName == "Large";
      default: // if in any mode from panel recognition (which is not stored), for example, return false
        return false;
    }
  }

  public colorFeaResult() {
    if (this.isResultAvailable()) {
      if (this.useScale && !!this.scale) {
        this.FeaResult.setColorConfiguration(this.scale.getScaleAdjustedColorConfiguration());
        this.FeaResult.render(this.graphicsMaterials, this.assetManager.asset.GetStructureRegions());
      }
      else if (this.useScale == false) {
        this.FeaResult.render(this.graphicsMaterials, this.assetManager.asset.GetStructureRegions());
      }
      else {
        // uses scale but for some reason scale is not available: do nothing
      }
    }
  }

  private async handleDateChange(dateChanged: boolean, isActive: boolean) {
    if (dateChanged && isActive) {
      await this.fetchAndRenderCurrentResult();
    }
    else if (dateChanged && !isActive) {
      if (this.isCurrentModeFeaResultMode()) {
        this.resetFeaResult();
        return;
      }
      
      this.clearFeaDataOnly();
    }
  }

  private async fetchAndRenderCurrentResult() {
    this.loadingResult = true;
    this.setDefaultIfNecessary();
    if (await this.hasAvailableResultsOnDate()) {
      var result = await this.feaProcessService.getProcessResult(this.currentProcessName, this.currentVectorName, this.currentByNode);
      this.handleCurrentProcessResult(result);
    }
    else {
      this.resetFeaResult();
    }
    this.loadingResult = false;
  }

  private async hasAvailableResultsOnDate(): Promise<boolean> {
    switch (this.currentProcessName) {
      case 'FEACriticalAreas':
        return await this.feaProcessService.hasAvailableResultsOnDate('FEACriticalAreas')
      default:
        return true; // for all others we try to get result (maybe through interpolation)
    }
  }

  private setDefaultIfNecessary() : void {
    if (!this.currentProcessName) {
      this.setCurrentResultParameters('Nastran', '', false); // default result to show
    }
    else if (this.currentProcessName == 'PanelRecognition' && this.assetManagerFea.latestFeaMode == 'FEAResults') { // TODO: temporary while panel and other results are not unified
      this.setCurrentResultParameters('Nastran', '', false); // default result to show
    }
  }

  private handleCurrentProcessResult(result) {
    if (!result) {
      this.assetManagerFea.setIsFeaResultActive(false);
      return;
    }

    this.title = result.title;
    this.currentProcessResult = result.processResult || null;
    this.instantiateFeaResult(result.valuesPerElement);
    this.instantiateScale();
    this.colorFeaResult();
    
    if (this.showMesh) {
      this.regenerateMeshForNewResult();
    }
  }

  private instantiateFeaResult(parsedata) {
    switch (this.currentProcessName) {
      case 'Nastran':
        this.FeaResult = new FeaResult(this.assetManager.asset, this.currentByNode, !this.currentByNode); // smooth it out if plotting by element
        this.FeaResult.populateFeaResult(parsedata);
        break;
      case 'DLA':
      case 'MOU':
      case 'FloatingDryDockChecks':
      case 'CodeChecks':
      case 'Reliability':
      case 'ThicknessUpdate':
      case 'Load':
        this.FeaResult = new FeaResult(this.assetManager.asset, false, false);
        this.FeaResult.populateFeaResult(parsedata);
        break;
      case 'FEACriticalAreas':
        var caColorConfig = new FeaSingleColorConfiguration('red');
        this.FeaResult = new FeaResult(this.assetManager.asset, false, false, caColorConfig);
        this.FeaResult.populateFeaResult(parsedata);
        break;
      case 'PanelRecognition':
        var panelColorConfig = new FeaSpectralColorConfiguration([0, 9]);
        this.FeaResult = new FeaResult(this.assetManager.asset, false, false, panelColorConfig);
        this.FeaResult.populateFeaResult(parsedata);
        break;
      default:
        break;
    }
  }

  private instantiateScale() {
    switch (this.currentProcessName) {
      case 'Nastran':
        this.useScale = true;
        this.scale = this.getResultScale(false, false);
        break;
      case 'DLA':
      case 'MOU':
      case 'FloatingDryDockChecks':
      case 'CodeChecks':
        this.useScale = true;
        this.scale = this.getResultScale(true, false);
        break;
      case 'Reliability':
        this.useScale = true;
        this.scale = this.getResultScale(true, true);
        break;
      case 'ThicknessUpdate':
        var colors: Map<string, number> = new Map();
        colors.set('lightblue', 0);
        colors.set('darkblue', 1);
        this.useScale = true;
        this.scale = this.getResultScale(false, false, colors);
        break;
      case 'Load':
        var colors: Map<string, number> = new Map();
        colors.set('white', 0);
        colors.set('navy', 1);
        this.useScale = true;
        this.scale = this.getResultScale(false, false, colors);
        break;
      case 'FEACriticalAreas':
      case 'PanelRecognition':
        this.useScale = false;
        this.scale = null;
        break;
      default:
        break;
    }
  }

  private getResultScale(isUnitary: boolean = false, useLog: boolean = false, colors: Map<string, number> = null) : ResultScale {
    const val = this.FeaResult.getMinMaxByRegionAndVisibility();
    let scale = new ResultScale(val['max'], val['min'], isUnitary, useLog, colors)
    if (!!this.scale){
      scale.High = this.scale.High
      scale.Low = this.scale.Low
    }
    return scale;
  }

  public hasProcessResultDetails(): boolean {
    return !!this.currentProcessResult;
  }

  /**
   * Toggle mesh visibility
   * @param show Whether to show the mesh lines
   */
  public toggleMesh(show: boolean): void {
    this.showMesh = show;
    
    if (!this.FeaResult) {
      console.warn('No FEA result available for mesh generation');
      return;
    }

    if (show) {
      const hasValidTopology = this.validateTopologyData();
      if (!hasValidTopology) {
        console.error('Cannot generate mesh: ElementTopology data not available');
        this.showMesh = false;
        return;
      }

      const existingMeshLines = this.FeaResult.getMeshLines();
      if (!existingMeshLines || existingMeshLines.length === 0) {
        const meshLines = this.feaMeshRenderer.generateMeshLines(this.FeaResult);
        
        this.FeaResult.setMeshLines(meshLines);
        
        meshLines.forEach(line => {
          this.graphics.scene.add(line);
        });
      }
      
      this.FeaResult.setShowMesh(true);
    } else {
      this.FeaResult.setShowMesh(false);
    }
  }

  /**
   * Validate if FEA result has valid topology data for mesh generation
   */
  private validateTopologyData(): boolean {
    for (const regionId in this.FeaResult.ElementsByRegion) {
      const elements = this.FeaResult.ElementsByRegion[regionId];
      for (const element of elements) {
        const topology = element.surfaceDesign?.userData?.ElementTopology;
        if (topology && topology.length > 0) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Clear mesh lines from scene and free memory without changing showMesh state
   */
  private clearMeshLines(): void {
    if (this.FeaResult) {
      this.FeaResult.disposeMeshLines();
    }
    this.feaMeshRenderer.dispose();

  }


  private subscribeToAssetStateChanges(): void {
    if (this.assetStateSubscription) {
      this.unsubscribeFromAssetStateChanges();
    }

    if (this.assetManager.asset?.assetStateList?.stateChanged) {
      this.assetStateSubscription = this.assetManager.asset.assetStateList.stateChanged.subscribe((update) => {
        if (update && this.showMesh && this.isResultAvailable()) {
          this.regenerateMesh();
        }
      });
    }
  }

  private unsubscribeFromAssetStateChanges(): void {
    if (this.assetStateSubscription) {
      this.assetStateSubscription.unsubscribe();
      this.assetStateSubscription = null;
    }
  }

  private regenerateMesh(): void {
    if (!this.FeaResult) {
      return;
    }

    if (this.FeaResult.getMeshLines()) {
      this.FeaResult.disposeMeshLines();
    }

    const meshLines = this.feaMeshRenderer.generateMeshLines(this.FeaResult);
    this.FeaResult.setMeshLines(meshLines);

    meshLines.forEach(line => {
      this.graphics.scene.add(line);
    });

    this.FeaResult.setShowMesh(this.showMesh);
  }

  private regenerateMeshForNewResult(): void {
    this.toggleMesh(this.showMesh);
  }
}
